import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Users, LayoutGrid, ListChecks, Inbox, Trophy, UserX } from 'lucide-react';

/**
 * Resumen del CRM en una sola tira, encima del Pipeline.
 *
 * Antes esto era la pantalla «Inicio»: seis tarjetas grandes de números y
 * cuatro botones que llevaban a Pipeline, Leads y Tareas — las mismas tres
 * pestañas que ya estaban justo arriba. Es decir, una pantalla entera cuyo
 * trabajo real era hacer que el usuario diera un clic más para llegar a donde
 * iba siempre. Ahora los números viven donde se trabaja.
 *
 * Dos reglas de las que depende que esto no estorbe:
 *
 *  1. Lo que se puede clicar se ve clicable, y lo que no, no. «Oportunidades
 *     abiertas» y «Ganadas» no llevan a ningún lado desde aquí (ya las estás
 *     viendo en el tablero de abajo), así que no tienen cursor de mano ni
 *     efecto al pasar por encima. Invitar a un clic que no hace nada es peor
 *     que no invitarlo.
 *  2. «Entrantes» siempre está, aunque marque cero. Es la única puerta a esa
 *     pantalla desde que dejó de estar en el menú, y quitarla al llegar a cero
 *     la dejaría inalcanzable justo el día que aparezca un caso raro.
 */

const Cifra = ({ icono: Icono, valor, etiqueta, color, onClick, resaltado, testid }) => {
  const contenido = (
    <>
      <Icono className={`w-4 h-4 ${color} flex-shrink-0`} />
      <span className="text-base font-bold text-[#0F172A] tabular-nums leading-none">{valor}</span>
      <span className="text-[11px] text-[#64748B] leading-tight">{etiqueta}</span>
    </>
  );
  const base = 'flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border whitespace-nowrap';
  if (!onClick) {
    return (
      <div className={`${base} border-[#E2E8F0] bg-white`} data-testid={testid}>{contenido}</div>
    );
  }
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`${base} transition-colors cursor-pointer ${
        resaltado ? 'border-amber-300 bg-amber-50 hover:bg-amber-100'
                  : 'border-[#E2E8F0] bg-white hover:bg-[#F8FAFC] hover:border-[#CBD5E1]'}`}>
      {contenido}
    </button>
  );
};

export default function CrmResumen({ onSinDatos }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // `silencioso`: es un dato de apoyo, no la razón por la que se abrió la
    // pantalla. Levantar el muñequito de carga a pantalla completa por unos
    // contadores taparía el tablero, que es lo que la persona vino a ver.
    api.get('/tenant/crm/status', { silencioso: true })
      .then(r => {
        setStats(r.data);
        onSinDatos?.(r.data?.crm_leads_count === 0 && r.data?.staged_inbound_count === 0);
      })
      .catch(() => setStats(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="crm-resumen">
      <Cifra icono={Inbox} valor={stats.staged_inbound_count ?? 0} etiqueta="por revisar"
        color={stats.staged_inbound_count > 0 ? 'text-amber-600' : 'text-[#94A3B8]'}
        resaltado={stats.staged_inbound_count > 0}
        onClick={() => navigate('/tenant/crm/pending')} testid="crm-resumen-entrantes" />
      <Cifra icono={Users} valor={stats.crm_leads_count ?? 0} etiqueta="leads"
        color="text-blue-500" onClick={() => navigate('/tenant/crm/leads')}
        testid="crm-resumen-leads" />
      <Cifra icono={UserX} valor={stats.unassigned_leads_count ?? 0} etiqueta="sin responsable"
        color={stats.unassigned_leads_count > 0 ? 'text-red-500' : 'text-[#94A3B8]'}
        onClick={() => navigate('/tenant/crm/leads?estado=unassigned')}
        testid="crm-resumen-sin-asignar" />
      <Cifra icono={LayoutGrid} valor={stats.open_deals_count ?? 0} etiqueta="abiertas"
        color="text-emerald-500" testid="crm-resumen-abiertas" />
      <Cifra icono={Trophy} valor={stats.won_deals_count ?? 0} etiqueta="ganadas"
        color="text-amber-500" testid="crm-resumen-ganadas" />
      <Cifra icono={ListChecks} valor={stats.pending_tasks_count ?? 0} etiqueta="tareas pendientes"
        color="text-purple-500" onClick={() => navigate('/tenant/crm/tasks')}
        testid="crm-resumen-tareas" />
    </div>
  );
}
